import React, { useState, useEffect, useRef } from "react";
import {
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { AdvancedFilters } from "../../types/filters";
import FormHelperText from "@mui/material/FormHelperText";

interface SelectDropdownProps {
  label: string;
  key: string;
  filterKey: string;
  options: {
    key: number | string;
    value: string;
    label?: string;
    tooltip: string;
  }[];
  onChange: (selected: string | string[]) => void;
  multiple?: boolean;
  isError?: boolean;
  value?: string | string[] | undefined; // <-- allow controlled value from parent
  filters?: AdvancedFilters;
  className?: string;
  disabled?: boolean;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  label,
  key,
  filterKey,
  options,
  onChange,
  multiple = false,
  value,
  filters,
  className = "",
  isError = false,
  disabled = false,
}) => {
  const initialValue =
    multiple && typeof value === "string" ? value.split(",") : value;

  const [selectedValue, setSelectedValue] = useState<
    string | string[] | undefined
  >(multiple ? initialValue || [] : value);

  const [isInnerTooltipOpen, setIsInnerTooltipOpen] = useState(false);

  const didMount = useRef(false);

  // Reset selected value if the available options change
  useEffect(() => {
    if (!Array.isArray(options) || selectedValue === undefined) return;
    if (multiple) {
      const validValues = (selectedValue as string[]).filter((val) =>
        options.some((option) => String(option.key) === String(val))
      );
      if (validValues.length !== (selectedValue as string[]).length) {
        setSelectedValue(validValues);
      }
    } else {
      const isValid = options.some(
        (option) => String(option.key) === String(selectedValue)
      );
      if (!isValid) {
        setSelectedValue(value);
      }
    }
  }, [options, selectedValue, multiple]);

  useEffect(() => {
    if (
      multiple &&
      options.length > 0 &&
      (selectedValue === undefined ||
        (Array.isArray(selectedValue) && selectedValue.length === 0)) &&
      !didMount.current
    ) {
      const allKeys = options.map((opt) => String(opt.key));
      setSelectedValue(allKeys);
      onChange(allKeys);
      didMount.current = true;
    }
    // eslint-disable-next-line
  }, [options, selectedValue, multiple, filters]);

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const newValue = event.target.value;
    // Prevent "__all__" from being set as a value
    if (Array.isArray(newValue) && newValue.includes("__all__")) {
      return;
    }
    // Update local state
    setSelectedValue(newValue);

    // For multiple select, convert array to comma-separated string before sending to parent
    if (multiple && Array.isArray(newValue)) {
      onChange(newValue.join(","));
    } else {
      onChange(newValue);
    }
  };

  const selectedOption = options.find((opt) => opt.key === selectedValue);
  return (
    <FormControl
      size="small"
      sx={{ minWidth: 100, maxWidth: 100 }}
      className={className}
      variant="outlined"
      error={isError}
    >
      <InputLabel>{label}</InputLabel>
      <Tooltip
        title={
          isInnerTooltipOpen
            ? ""
            : options.find(
                (option) => String(option.key) === String(selectedValue)
              )?.tooltip || ""
        }
        arrow
        placement="top"
        disableHoverListener={!selectedValue} // disable when nothing selected
      >
        <Select
          label={label}
          disabled={disabled}
          value={multiple ? selectedValue : value}
          onChange={handleChange}
          multiple={multiple}
          renderValue={(selected) => {
            if (multiple) {
              const selectedArr = selected as string[];
              if (selectedArr.length === options.length) {
                return "All selected";
              }
              if (selectedArr.length > 1) {
                return `${selectedArr.length} selected`;
              }
              return selectedArr
                .map(
                  (val) =>
                    options.find((option) => String(option.key) === String(val))
                      ?.value || val
                )
                .join(", ");
            }
            return (
              options.find((option) => String(option.key) === String(selected))
                ?.value || ""
            );
          }}
          sx={{
            borderRadius: "8px",
            fontSize: "11px",
            background: "white",
            maxHeight: 25, // Optional: force the box to stay short
            overflow: "hidden",
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                maxWidth: 200, // or any value you prefer for the dropdown menu
              },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          }}
        >
          {options.length > 0 ? (
            options.map((option: any) => {
              return (
                <MenuItem
                  key={String(option.key)}
                  value={String(option.key)}
                  sx={{
                    height: "30px",
                    py: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                    pl: 1,
                  }}
                >
                  <Tooltip
                    key={option.value}
                    title={option.tooltip}
                    placement="right"
                    onOpen={() => setIsInnerTooltipOpen(true)}
                    onClose={() => setIsInnerTooltipOpen(false)}
                    arrow
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        pointerEvents: "auto", // Ensure tooltip works
                      }}
                    >
                      {option.value}
                    </span>
                  </Tooltip>
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled>No options available</MenuItem>
          )}
        </Select>
      </Tooltip>
      <span style={{ height: "5px" }}>
        {isError && <FormHelperText>Required</FormHelperText>}
      </span>
    </FormControl>
  );
};

export default SelectDropdown;
